import {getSsoSessionToken} from '../interceptors/auth_interceptor';
import {getSessionTokenCookieName} from './session_cookie';

/**
 * Options for generating SSO authentication URLs.
 */
export interface SsoUrlOptions {
  logout?: boolean;
}

/**
 * Interface representing the structure of claims decoded from the SSO token.
 */
export interface SsoTokenClaims {
  email?: string;
}

/**
 * Checks if the application is running on localhost.
 */
export function isLocal(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Checks if the browser is controlled by automation (E2E tests).
 */
export function isAutomated(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return !!navigator.webdriver;
}

/**
 * Determines if the authentication check should be bypassed.
 * Bypassed in local development or during automated E2E testing,
 * unless force_login=true query parameter is present.
 */
export function shouldBypassAuth(): boolean {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('force_login') === 'true') {
      return false;
    }
  }
  return isLocal() || isAutomated();
}

/**
 * Checks if a valid SSO session token cookie is present.
 *
 * Used by the `authGuard` to quickly determine if the user has an active session
 * without making a network request. If the session cookie for the current environment
 * is present and non-empty, we assume the user is authenticated in the frontend.
 * The actual token validation occurs at the backend/API gateway level where the
 * `AuthInterceptor` attaches this token to the `Authorization` header of outgoing requests.
 */
export function hasValidSession(): boolean {
  const token = getSsoSessionToken();
  return token !== null && token !== '';
}

/**
 * Constructs the SSO login URL with redirect parameter and optional logout flag.
 *
 * @param currentUrl The current application URL to redirect back to.
 * @param options Optional configuration (e.g., logout flag).
 * @returns The constructed SSO URL.
 */
export function getSsoUrl(currentUrl: string, options?: SsoUrlOptions): string {
  const url = new URL(currentUrl);
  const hostname = url.hostname ?? '';
  const ssoHostname =
    hostname === 'maisenovich.xtapestry.app'
      ? 'sso.experimental-platform.xtapestry.app'
      : `sso.${hostname}`;
  const ssoUrl = new URL(url.toString());
  ssoUrl.hostname = ssoHostname;
  ssoUrl.pathname = '/';
  ssoUrl.searchParams.set('redirect_url', currentUrl);

  if (options?.logout) {
    ssoUrl.searchParams.set('logout', 'true');
  }
  return ssoUrl.toString();
}

/**
 * Constructs the token renewal URL for the hidden session refresh iframe.
 *
 * In the GCIP/SSO architecture, loading the sso-login subdomain (e.g. https://sso.dev.xtapestry.app/)
 * inside a hidden iframe boots the Firebase Auth SDK in the background, which detects the active session,
 * fetches a renewed ID token, and writes the sso_session_token cookie to the shared parent domain (.xtapestry.app).
 *
 * @param currentHref Optional URL to extract hostname from (defaults to window.location.href).
 * @returns The constructed SSO refresh URL, or null if not running on an SSO domain.
 */
export function getRefreshUrl(currentHref?: string): string | null {
  const href = currentHref ?? (typeof window !== 'undefined' ? window.location.href : undefined);
  if (!href) {
    return null;
  }
  try {
    const url = new URL(href);
    const hostname = url.hostname;
    if (hostname === 'maisenovich.xtapestry.app') {
      return `${url.protocol}//sso.experimental-platform.xtapestry.app/`;
    }
    if (
      hostname === 'xtapestry.app' ||
      hostname.endsWith('.xtapestry.app') ||
      hostname === 'localhost'
    ) {
      const port = url.port ? `:${url.port}` : '';
      return `${url.protocol}//sso.${hostname}${port}/`;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Decodes the payload of a JSON Web Token (JWT) without verification.
 * It safely splits the token, performs Base64 URL decoding, and parses the JSON payload.
 *
 * @param token The encoded JWT string.
 * @returns The decoded payload object as SsoTokenClaims, or null if decoding fails.
 */
export function decodeJwt(token: string): SsoTokenClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload) as SsoTokenClaims;
  } catch {
    return null;
  }
}

/**
 * Calculates the parent domain for setting cookies.
 *
 * For example, if the hostname is 'app.tapestry.google.com', it returns '.app.tapestry.google.com'.
 * For 'localhost' or IP addresses, it returns an empty string.
 *
 * @returns The parent domain string, starting with a dot, or an empty string.
 */
export function getParentDomain(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^[0-9.]+$/.test(hostname)) {
    return '';
  }
  return '.' + hostname;
}

/**
 * Clears the SSO session token cookie for the environment currently being served,
 * on both the parent domain and the host-only scope.
 *
 * Only this environment's cookie is cleared. A session cookie inherited from the
 * apex domain belongs to another environment, and expiring it here would sign the
 * user out of that environment too.
 */
export function clearSsoCookie() {
  const parentDomain = getParentDomain();
  const cookieName = getSessionTokenCookieName();

  if (parentDomain !== '') {
    document.cookie = `${cookieName}=; path=/; domain=${parentDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}
